// src/components/Publico/AltaNuevoIngreso.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Divider,
  Link,
} from "@mui/material";
import { Formik, Form } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import logocolegio from "../../img/logocolegio.png";
import logoEnfernet from "../../img/logoENFERNETlogin.png";
import { validarIBAN } from "../Utiles/Verificar/iban";
import { validarDNI } from "../Utiles/Verificar/dni";
import { validarEmail } from "../Utiles/Verificar/email";
import SelectPais, { paises } from "../common/Paises";
import SelectProvincia, { provincias } from "../common/Provincias";
import SelectTitulacionPrincipal from "../common/TitulacionPrincipal";
import SelectEstado from "../common/Estados";
import SelectSituacionProfesional from "../common/SituacionProfesional";
import Autocomplete from "@mui/material/Autocomplete";
import SelectAmbitoTrabajo from "../common/AmbitoTrabajo";
import SelectEspecialidades from "../common/Especialidades";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InformacionProteccionDatos from "../info/InformacionProteccionDatos";
import { obtenerBICdesdeBanco } from "../common/Bics";
import { useNavigate } from "react-router-dom";

const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

const ALTA_NUEVO_INGRESO_ID = 3;
const REINGRESO_ID = 4;
const TRASLADO_ID = 6;

const REQUIRED_TRASLADO = ["solicitud", "autorizacion", "sepa"];
const REQUIRED_DEFAULT = [
  "solicitud",
  "dni",
  "titulo",
  "certificacion",
  "autorizacion",
  "pago",
  "sepa",
  "foto",
];

const etiquetasArchivos = {
  solicitud: "Solicitud de Alta Firmada Electrónicamente (PDF)",
  dni: "DNI/NIE",
  titulo: "Título de Enfermería o Certificación Supletoria Provisional",
  certificacion: "Certificación académica de notas",
  autorizacion:
    "Autorización para consultar Registro Nacional de Títulos Universitarios (PDF)",
  pago: "Justificante de pago",
  sepa: "Autorización de domiciliación bancaria (PDF)",
  foto: "Foto tamaño carnet",
};

const nombreArchivos = {
  solicitud: "solicitud_alta",
  dni: "dni_nie",
  titulo: "titulo_enfermeria",
  certificacion: "certificado_notas",
  autorizacion: "autorizacion_consulta_titulos",
  pago: "justificante_pago",
  sepa: "sepa",
  foto: "foto",
};

const extraerDatosIban = (iban) => {
  const banco = iban.slice(4, 8);
  const sucursal_Bancaria = iban.slice(8, 12);
  const digito_Control = iban.slice(12, 14);
  const cuenta_Bancaria = iban.slice(14, 24);
  return { banco, sucursal_Bancaria, digito_Control, cuenta_Bancaria };
};

const handleFileChange = (e, nombreCampo, setArchivos) => {
  setArchivos((prev) => ({
    ...prev,
    [nombreCampo]: e.target.files?.[0] || null,
  }));
};

async function loadPdfFromFrontOrUrl(pathOrUrl) {
  const url = /^https?:\/\//i.test(pathOrUrl)
    ? pathOrUrl
    : new URL(pathOrUrl, window.location.origin).href;

  const res = await fetch(url, { cache: "no-store" });
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!res.ok || !ct.includes("application/pdf")) {
    throw new Error(
      `No es un PDF válido o no se encontró: url=${url} status=${res.status} content-type=${ct}`
    );
  }
  return res.arrayBuffer();
}

/* ================== PDFs ================== */
async function generarPDFSolicitudIngreso(values) {
  const existingPdfBytes = await loadPdfFromFrontOrUrl("/Solicitud_ingreso.pdf");
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const page = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const draw = (text, x, y, size = 10) => {
    if (text !== undefined && text !== null && String(text).trim() !== "") {
      page.drawText(String(text), { x, y, size, font, color: rgb(0, 0, 0) });
    }
  };

  draw(
    `${values.nombre} ${values.apellido1 || ""} ${values.apellido2 || ""}`.trim(),
    112.07,
    707
  );
  draw(values.localidadNacimiento || "", 123.56, 692);

  const codigo = values.provinciaNacimiento || values.provincia;
  const provinciaNombre =
    provincias.find((p) => String(p.codigo) === String(codigo))?.nombre || "";
  draw(provinciaNombre, 305.12, 692);

  if (values.fechaNacimiento) {
    const f = new Date(values.fechaNacimiento);
    draw(
      `${String(f.getDate()).padStart(2, "0")}/${String(f.getMonth() + 1).padStart(
        2,
        "0"
      )}/${f.getFullYear()}`,
      457,
      692
    );
  }

  draw(values.dni || "", 140.2, 675);
  draw(values.direccion || "", 290.74, 675);
  draw(values.poblacion || "", 135, 659);
  draw(values.provincia || "", 380, 659);
  draw(values.cp || "", 95, 643);
  draw(values.telefono || "", 185, 642);
  draw(values.email || "", 322, 640);
  draw(values.universidad || "", 273, 623);
  draw(values.anoFin || "", 122, 606);
  draw(values.entidadBancaria || "", 254, 568);
  draw(values.iban || "", 130, 552);

  const hoy = new Date();
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  draw(String(hoy.getDate()), 314, 149);
  draw(meses[hoy.getMonth()], 353, 149);
  draw(String(hoy.getFullYear()), 487, 149);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Solicitud_Ingreso.pdf");
}

async function generarPDFRegistro(values) {
  const existingPdfBytes = await loadPdfFromFrontOrUrl("/registro_nacional.pdf");
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const page = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const draw = (text, x, y, size = 12) => {
    if (text !== undefined && text !== null && String(text).trim() !== "") {
      page.drawText(String(text), { x, y, size, font, color: rgb(0, 0, 0) });
    }
  };

  draw(
    `${values.nombre} ${values.apellido1 || ""} ${values.apellido2 || ""}`.trim(),
    140,
    693
  );
  draw(values.dni || "", 140, 665);

  const direccion = `${values.direccion || ""}`.trim();
  const poblacion = values.poblacion ? `en ${values.poblacion}` : "";
  draw(`${direccion} ${poblacion}`.trim(), 160, 637);

  draw(values.telefono || "", 140, 610);
  draw(values.email || "", 200, 580);

  const hoy = new Date();
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  draw(String(hoy.getDate()), 340, 282);
  draw(meses[hoy.getMonth()], 395, 282);
  draw(String(hoy.getFullYear()).slice(-2), 522, 282);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "Registro_Nacional.pdf");
}

/* ====== Mandatos (TMP/preview) ====== */
async function crearMandatoTmpPublico({
  colegiadoCodigo = null,
  nif,
  iban,
  bic,
  fechaFirma,
  docSepaId = null,
}) {
  const body = { colegiadoCodigo, nif, iban, bic, fechaFirma, docSepaId };
  const { data } = await axios.post(`${API}/api/mandatos/tmp/publico`, body);
  const mndtId = data?.mandatoId ?? data?.mndtId ?? data?.mndt_id ?? null;
  return { mndtId, raw: data };
}

async function previewMandatoId(nif, iban, bic) {
  try {
    const { data } = await axios.get(`${API}/api/mandatos/preview`, {
      params: { nif, iban, bic },
    });
    return data?.mandatoId || null;
  } catch {
    return null;
  }
}

async function generarPDFSepa(values) {
  const existingPdfBytes = await loadPdfFromFrontOrUrl("/SEPA.pdf");
  const ibanLimpio = (values.iban || "").replace(/\s+/g, "").toUpperCase();
  const { banco } = extraerDatosIban(ibanLimpio);
  const bic = (obtenerBICdesdeBanco(banco) || "").toUpperCase();

  let mandatoIdParaPdf = null;
  try {
    const { mndtId } = await crearMandatoTmpPublico({
      colegiadoCodigo: null,
      nif: values.dni,
      iban: ibanLimpio,
      bic,
      fechaFirma: dayjs().format("YYYY-MM-DD"),
      docSepaId: null,
    });
    mandatoIdParaPdf = mndtId || null;
  } catch (e) {
    try {
      mandatoIdParaPdf = await previewMandatoId(values.dni, ibanLimpio, bic);
    } catch { }
  }
  if (!mandatoIdParaPdf) {
    try {
      mandatoIdParaPdf = await previewMandatoId(values.dni, ibanLimpio, bic);
    } catch { }
  }

  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const page = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const draw = (text, x, y, size = 10) => {
    if (text !== undefined && text !== null && String(text).trim() !== "") {
      page.drawText(String(text), { x, y, size, font, color: rgb(0, 0, 0) });
    }
  };

  const paisNombre =
    paises.find((p) => Number(p.id) === Number(values.paisId))?.nombre || "";

  const nombreCompleto = `${(values.nombre || "").toUpperCase()} ${(values.apellido1 || "").toUpperCase()} ${(
    values.apellido2 || ""
  ).toUpperCase()}`.trim();

  const direccionUP = `${(values.direccion || "").toUpperCase()} ${values.cp ? `(${values.cp})` : ""}`.trim();

  const direccionDown = `${values.cp || ""} ${values.poblacion || ""} ${values.provincia || ""}`.trim().toUpperCase();

  const pais = `${(paisNombre || "").toUpperCase()}`.trim();

  draw(nombreCompleto, 340, 760);
  draw(direccionUP, 340, 745);
  draw(direccionDown, 340, 730);
  draw(pais, 340, 715);
  draw(`Tel.: ${values.telefono || ""}`, 340, 700);
  draw(values.email || "", 340, 685);

  draw(`${mandatoIdParaPdf || "SIN ID DISPONIBLE"}`, 299, 604);

  draw(nombreCompleto, 340, 300);
  draw(direccionUP, 340, 265);
  draw(direccionDown, 340, 250);
  draw(pais, 340, 235);
  draw(banco || "", 66, 185);
  draw(bic || "", 170, 185);
  draw(ibanLimpio || "", 300, 185);

  const hoy = new Date();
  const dia = String(hoy.getDate()).padStart(2, "0");
  const mes = String(hoy.getMonth() + 1).padStart(2, "0");
  const anio = hoy.getFullYear();
  draw(`${dia}/${mes}/${anio}`, 200, 134);

  const pdfBytes = await pdfDoc.save();
  saveAs(new Blob([pdfBytes], { type: "application/pdf" }), "SEPA.pdf");
}

/* ================= Component ================= */
const AltaNuevoIngreso = () => {
  const [archivos, setArchivos] = useState({});
  const [subiendo, setSubiendo] = useState(false);
  const [lugaresTrabajo, setLugaresTrabajo] = useState([]);
  const [cargandoLugares, setCargandoLugares] = useState(false);

  // nifExiste ahora solo informativo; NO bloquea salvo alta nuevo ingreso
  const [nifExiste, setNifExiste] = useState(false);
  const [errorNif, setErrorNif] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLugares = async () => {
      setCargandoLugares(true);
      try {
        const { data } = await axios.get(`${API}/api/lugares-trabajo`);
        setLugaresTrabajo(data || []);
      } catch (error) {
        console.error("Error cargando lugares de trabajo:", error);
      } finally {
        setCargandoLugares(false);
      }
    };
    fetchLugares();
  }, []);

  const initialValues = useMemo(
    () => ({
      nombre: "",
      apellido1: "",
      apellido2: "",
      dni: "",
      fechaNacimiento: "",
      nacionalidad: "",
      sexo: "",

      direccion: "",
      poblacion: "",
      provincia: "",
      cp: "",
      paisId: "",
      telefono: "",
      email: "",

      paisNacimientoId: "",
      provinciaNacimiento: "",
      localidadNacimiento: "",

      password: "",
      estado: "",

      titulacionPrincipalId: "",
      universidad: "",
      anoFin: "",

      entidadBancaria: "",
      iban: "",

      situacionProfesionalId: "",
      situacion: "",

      lugarTrabajoId: "",
      ambitoTrabajoId: "",
      especialidadesIds: [],
    }),
    []
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        nombre: Yup.string().required("Campo obligatorio"),
        dni: Yup.string()
          .required("Campo obligatorio")
          .test("dni-valido", "Documento no válido", (value) => validarDNI(value).valido),
        email: Yup.string()
          .required("Campo obligatorio")
          .test("email-valido", "Email inválido", (value) => validarEmail(value)),
        telefono: Yup.string().required("Campo obligatorio"),
        iban: Yup.string()
          .required("Campo obligatorio")
          .test("iban-valido", "IBAN inválido", (value) => validarIBAN(value)),
        direccion: Yup.string().required("Campo obligatorio"),
        estado: Yup.string().required("Selecciona un estado"),
      }),
    []
  );

  // NUEVO: solo bloquea en Alta Nuevo Ingreso si existe
  const validarNifRemoto = async (nif, estadoActual) => {
    if (!nif) return;

    const estadoId = Number(estadoActual);
    const esAltaNuevoIngreso = estadoId === ALTA_NUEVO_INGRESO_ID;

    try {
      const { data: yaExiste } = await axios.get(`${API}/colegiado/existe-nif?nif=${nif}`);
      const existe = !!yaExiste;

      setNifExiste(existe);

      if (esAltaNuevoIngreso && existe) {
        setErrorNif("❌ Alta Nuevo Ingreso: ya existe un colegiado con este DNI.");
      } else {
        setErrorNif(null);
      }
    } catch (error) {
      console.error("Error al verificar el NIF:", error);
      setNifExiste(false);
      setErrorNif("❌ No se pudo verificar el NIF.");
    }
  };

  const handleRegistroYSubida = async (values) => {
    setSubiendo(true);
    try {
      const estadoId = Number(values.estado);
      const esAltaNuevoIngreso = estadoId === ALTA_NUEVO_INGRESO_ID;

      // 1) Check NIF
      const { data: existeData } = await axios.get(`${API}/colegiado/existe-nif?nif=${values.dni}`);
      const existe = !!existeData;
      setNifExiste(existe);

      // ❌ Solo bloquea duplicado si es Alta Nuevo Ingreso (3)
      if (esAltaNuevoIngreso && existe) {
        alert("❌ Ya existe un colegiado con este NIF. No se puede tramitar Alta Nuevo Ingreso.");
        return;
      }

      // 2) Datos bancarios
      const ibanLimpio = (values.iban || "").replace(/\s+/g, "").toUpperCase();
      const { banco, sucursal_Bancaria, digito_Control, cuenta_Bancaria } =
        extraerDatosIban(ibanLimpio);
      const bicCalculado = (obtenerBICdesdeBanco(banco) || "").toUpperCase();
      const hoyIso = dayjs().format("YYYY-MM-DD");

      // Literales
      const provinciaNacimientoLiteral =
        provincias.find((p) => String(p.codigo) === String(values.provinciaNacimiento))?.nombre ||
        values.provinciaNacimiento ||
        "";

      const paisResidenciaLiteral =
        paises.find((p) => Number(p.id) === Number(values.paisId))?.nombre || "";

      const nacionalidadLiteral =
        paises.find((p) => Number(p.id) === Number(values.nacionalidad))?.nombre ||
        values.nacionalidad ||
        "";

      const situacionProfesionalIdNum =
        values.situacionProfesionalId !== "" && values.situacionProfesionalId !== null
          ? Number(values.situacionProfesionalId)
          : null;

      const titulacionPrincipalIdNum =
        values.titulacionPrincipalId !== "" && values.titulacionPrincipalId !== null
          ? Number(values.titulacionPrincipalId)
          : null;

      // 3) Payload
      const payload = {
        nombre: (values.nombre || "").toUpperCase(),
        apellido_1: (values.apellido1 || "").toUpperCase(),
        apellido_2: (values.apellido2 || "").toUpperCase(),
        nif: (values.dni || "").toUpperCase(),
        email: (values.email || "").trim(),
        password: values.password || "",
        rol: "PENDIENTE",

        fecha_nacimiento: values.fechaNacimiento || "",
        pais_nacimiento_id: values.paisNacimientoId ? Number(values.paisNacimientoId) : null,
        provincia_nacimiento: provinciaNacimientoLiteral,
        localidad_nacimiento: values.localidadNacimiento || "",

        nacionalidad: nacionalidadLiteral,

        direccion: (values.direccion || "").toUpperCase(),
        localidad: values.poblacion || "",
        c_p_: values.cp || "",
        provincia: values.provincia || "",
        pais: paisResidenciaLiteral,
        pais_id: values.paisId ? Number(values.paisId) : null,
        telefono_1: values.telefono || "",

        banco,
        sucursal_bancaria: sucursal_Bancaria,
        cuenta_bancaria: cuenta_Bancaria,
        digito_control: digito_Control,
        iban: ibanLimpio,
        bic: bicCalculado,
        remesable: 1,

        sexo: values.sexo || "",

        estado_id: values.estado ? Number(values.estado) : null,
        habilitacion_id: 1,

        situacion_profesional_id: situacionProfesionalIdNum,
        situacionProfesionalId: situacionProfesionalIdNum,
        situacion: situacionProfesionalIdNum,

        tipo_pago_id: 1,

        titulacion_principal_id: titulacionPrincipalIdNum,
        titulacionPrincipalId: titulacionPrincipalIdNum,
        titulacion_principal: titulacionPrincipalIdNum,

        universidad: (values.universidad || "").toUpperCase(),
        ano_fin: values.anoFin || "",

        lugares_trabajo_ids: values.lugarTrabajoId ? [Number(values.lugarTrabajoId)] : [],
        ambitos_trabajo_ids: values.ambitoTrabajoId ? [Number(values.ambitoTrabajoId)] : [],
        especialidades_ids: (values.especialidadesIds || []).map((x) => Number(x)),

        fecha_1a_colegiacion: hoyIso,
      };

      // 4) ✅ Decide endpoint:
      // - Si NIF existe => endpoint "update/trámite"
      // - Si no existe  => endpoint "crear"
      //
      // 👇 IMPORTANTE:
      // Ajusta estas rutas a las reales de tu backend:
      //   - crear:   POST /colegiado/publico
      //   - update:  POST /colegiado/publico/reingreso  (o /publico/tramite)
      //
      // Si quieres diferenciar traslado/reingreso en backend, puedes cambiar aquí:
      //   if (estadoId === REINGRESO_ID) -> /publico/reingreso
      //   else -> /publico/tramite
      const urlCrear = `${API}/colegiado/publico`;

      // Por defecto, si existe => usamos el endpoint “reingreso” que ya tienes.
      // Si más adelante creas /publico/tramite para trasladados, aquí lo cambias.
      const urlUpdate = `${API}/colegiado/publico/reingreso`;
      const urlFinal = existe
        ? `${API}/colegiado/publico/reingreso`
        : `${API}/colegiado/publico`;

      const res = await axios.post(urlFinal, payload);

      const token = res.data?.tokenTemporal;
      const savedCodigo = res.data?.colegiadoId;
      if (!token) throw new Error("No se recibió tokenTemporal del backend");

      // 5) Subida de documentos
      const esTraslado = Number(values.estado) === TRASLADO_ID;
      const requeridos = esTraslado ? REQUIRED_TRASLADO : REQUIRED_DEFAULT;

      const formData = new FormData();
      formData.append("token", token);

      for (const clave of requeridos) {
        const file = archivos[clave];
        if (!file) throw new Error(`Falta el archivo: ${clave}`);
        const extension = (file.name.split(".").pop() || "").toLowerCase();
        const nuevoNombre = `${nombreArchivos[clave]}.${extension}`;
        const renamedFile = new File([file], nuevoNombre, { type: file.type });
        formData.append("archivos", renamedFile);
      }

      await axios.post(`${API}/api/documentos/subir-externo`, formData);

      try {
        await axios.post(`${API}/api/documentos/asignar-foto?token=${token}`);
      } catch { }

      // Intentar identificar el PDF SEPA subido
      let sepaDocumentId = null;
      try {
        const { data: docs } = await axios.get(`${API}/api/documentos/listar-externo`, {
          params: { token },
        });
        const sepaDoc = (docs || []).find((d) =>
          ((d.nombre || d.filename || "") + "").toLowerCase().includes("sepa")
        );
        sepaDocumentId = sepaDoc?.id ?? null;
      } catch { }

      // Vincular TMP previo (si existe)
      try {
        await axios.post(`${API}/api/mandatos/tmp/bind`, {
          colegiadoCodigo: savedCodigo,
          nif: values.dni,
          iban: ibanLimpio,
          docSepaId: sepaDocumentId,
        });
      } catch { }

      // Crear (o asegurar) mandato temporal
      try {
        await crearMandatoTmpPublico({
          colegiadoCodigo: savedCodigo,
          nif: values.dni,
          iban: ibanLimpio,
          bic: bicCalculado,
          fechaFirma: hoyIso,
          docSepaId: sepaDocumentId,
        });
      } catch { }

      alert("✅ Solicitud enviada con éxito.");
      navigate("/login");
    } catch (error) {
      const backend =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        JSON.stringify(error?.response?.data || {});
      console.error("Error al enviar solicitud:", error?.response?.status, error?.response?.data, error);
      alert(`❌ Error al enviar la solicitud: ${backend || error.message}`);
    } finally {
      setSubiendo(false);
    }
  };

  const enlaceTraslado =
    "https://colegioenfermeriavalladolid.com/wp-content/uploads/2025/11/Sol_traslado_nueva.pdf";

  return (
    <Box className="transition-all duration-300 bg-gradient-to-br from-blue-200 via-blue-100 to-blue-50 min-h-screen w-full">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 5, gap: 4, alignItems: "center" }}>
            <img src={logocolegio} alt="Logo Colegio" style={{ width: 300, height: "auto" }} />
            <img src={logoEnfernet} alt="Logo EnferNET" style={{ width: "auto", height: "220px" }} />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" fontSize="2rem" color="#1976d2" gutterBottom>
              <strong>Primera incorporación al Colegio de Enfermería</strong>
            </Typography>
            <Typography variant="body1" fontSize="1.2rem" gutterBottom>
              Los enfermeros que no se hayan colegiado previamente en ningún Colegio Oficial de Enfermería deberán pagar la cuota
              de ingreso (37€) para iniciar el trámite.
            </Typography>
            <Typography variant="body1" fontSize="1.2rem" gutterBottom>
              El pago de la cuota de ingreso es requisito imprescindible para comenzar el trámite de la Colegiación.
            </Typography>
            <Typography variant="body1" fontSize="1.2rem" gutterBottom>
              El pago se realizará por transferencia bancaria o ingreso en cuenta, a nombre del Colegio Oficial de Diplomados de Enfermería de Valladolid,
              Bankinter –agencia principal- calle María de Molina nº7, con IBAN:{" "}
              <strong>ES04 0128 0200 9701 0011 5404</strong> , indicando nombre y apellidos de solicitante, y en concepto: Nombre y Apellidos.
            </Typography>
          </Box>

          <Typography variant="h5" gutterBottom>
            <strong>Formulario de Solicitud</strong>
          </Typography>
          <Typography variant="body1" fontSize="1.2rem" gutterBottom>
            <p>Tras rellenar el formulario, podrás descargar los PDF necesarios con los datos introducidos.</p>
            <p>Firma electrónicamente y adjunta los documentos antes de enviar la solicitud.</p>
          </Typography>

          <Formik initialValues={initialValues} validationSchema={validationSchema}>
            {({ values, handleChange, setFieldValue, errors, touched, isValid, dirty }) => {
              const estadoId = Number(values.estado);
              const modoTraslado = estadoId === TRASLADO_ID;
              const esAltaNuevoIngreso = Number(values.estado) === ALTA_NUEVO_INGRESO_ID;

              const requeridos = modoTraslado ? REQUIRED_TRASLADO : REQUIRED_DEFAULT;
              const allFilesPresent = requeridos.every((k) => archivos[k]);

              // ✅ Solo bloquea duplicado en alta nuevo ingreso
              const bloqueaPorNif = esAltaNuevoIngreso && nifExiste;

              return (
                <Form>
                  {/* ESTADO */}
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
                    <SelectEstado
                      name="estado"
                      value={values.estado}
                      onChange={(e) => {
                        handleChange(e);
                        // revalidar nif si ya está escrito
                        if (values.dni) validarNifRemoto(values.dni, e.target.value);
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* DATOS PERSONALES */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
                    Datos personales
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <TextField
                      name="nombre"
                      label="Nombre"
                      value={values.nombre}
                      onChange={handleChange}
                      error={touched.nombre && !!errors.nombre}
                      helperText={touched.nombre && errors.nombre}
                    />
                    <TextField
                      name="apellido1"
                      label="Primer Apellido"
                      value={values.apellido1}
                      onChange={handleChange}
                    />
                    <TextField
                      name="apellido2"
                      label="Segundo Apellido"
                      value={values.apellido2}
                      onChange={handleChange}
                    />
                    <TextField
                      name="dni"
                      label="DNI/NIE/Pasaporte"
                      value={values.dni}
                      onChange={(e) => {
                        handleChange(e);
                        validarNifRemoto(e.target.value, values.estado);
                      }}
                      error={!!errorNif}
                      helperText={
                        errorNif ||
                        (!esAltaNuevoIngreso && nifExiste
                          ? "ℹ️ El DNI ya existe: se tramitará como actualización (reingreso/traslado/etc.) con el estado seleccionado."
                          : "")
                      }
                    />

                    <SelectPais
                      name="paisNacimientoId"
                      label="País de nacimiento"
                      value={values.paisNacimientoId}
                      onChange={handleChange}
                    />
                    {String(values.paisNacimientoId) === "1" && (
                      <SelectProvincia
                        name="provinciaNacimiento"
                        label="Provincia de nacimiento"
                        value={values.provinciaNacimiento}
                        onChange={handleChange}
                      />
                    )}
                    <TextField
                      name="localidadNacimiento"
                      label="Localidad de nacimiento"
                      value={values.localidadNacimiento}
                      onChange={handleChange}
                    />
                    <TextField
                      name="fechaNacimiento"
                      label="Fecha de nacimiento"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={values.fechaNacimiento}
                      onChange={handleChange}
                    />

                    <SelectPais
                      name="nacionalidad"
                      label="Nacionalidad"
                      value={values.nacionalidad}
                      onChange={handleChange}
                    />

                    <TextField
                      name="password"
                      label="Contraseña"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      helperText={!esAltaNuevoIngreso ? "En trámites/actualizaciones puedes dejarla vacía si no quieres cambiarla." : ""}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ACADÉMICOS */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
                    Datos académicos
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <SelectTitulacionPrincipal
                      name="titulacionPrincipalId"
                      label="Titulación Principal"
                      value={values.titulacionPrincipalId}
                      onChange={handleChange}
                    />
                    <TextField
                      name="universidad"
                      label="Universidad"
                      value={values.universidad}
                      onChange={handleChange}
                    />
                    <TextField name="anoFin" label="Año finalización" value={values.anoFin} onChange={handleChange} />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* CONTACTO */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
                    Datos de contacto
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <TextField
                      name="email"
                      label="Email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                    />
                    <TextField
                      name="telefono"
                      label="Teléfono"
                      value={values.telefono}
                      onChange={handleChange}
                      error={touched.telefono && !!errors.telefono}
                      helperText={touched.telefono && errors.telefono}
                    />
                    <TextField
                      name="direccion"
                      label="Dirección COMPLETA (calle y número, piso, etc.)"
                      value={values.direccion}
                      onChange={handleChange}
                      error={touched.direccion && !!errors.direccion}
                      helperText={touched.direccion && errors.direccion}
                    />
                    <TextField name="poblacion" label="Población" value={values.poblacion} onChange={handleChange} />
                    <TextField name="provincia" label="Provincia" value={values.provincia} onChange={handleChange} />
                    <TextField name="cp" label="Código Postal" value={values.cp} onChange={handleChange} />
                    <SelectPais name="paisId" label="País de Residencia" value={values.paisId} onChange={handleChange} />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* BANCARIOS */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
                    Datos bancarios
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <TextField
                      name="entidadBancaria"
                      label="Entidad Bancaria"
                      value={values.entidadBancaria}
                      onChange={handleChange}
                    />
                    <TextField
                      name="iban"
                      label="IBAN (sin espacios)"
                      value={values.iban}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "iban",
                            value: (e.target.value || "").replace(/\s+/g, "").toUpperCase(),
                          },
                        })
                      }
                      error={touched.iban && !!errors.iban}
                      helperText={touched.iban && errors.iban}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* PROFESIONALES */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
                    Datos profesionales
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <SelectSituacionProfesional
                      name="situacionProfesionalId"
                      value={values.situacionProfesionalId}
                      onChange={(e) => {
                        const v = e?.target?.value ?? "";
                        handleChange(e);
                        setFieldValue("situacionProfesionalId", v);
                        setFieldValue("situacion", v);
                      }}
                    />

                    <Autocomplete
                      options={lugaresTrabajo}
                      loading={cargandoLugares}
                      getOptionLabel={(option) => option?.nombre || ""}
                      value={lugaresTrabajo.find((opt) => String(opt.id) === String(values.lugarTrabajoId)) || null}
                      onChange={(_, nuevoValor) => {
                        setFieldValue("lugarTrabajoId", nuevoValor ? nuevoValor.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Lugar de trabajo" placeholder="Escribe para buscar..." />
                      )}
                    />

                    <SelectAmbitoTrabajo name="ambitoTrabajoId" value={values.ambitoTrabajoId} onChange={handleChange} />
                    <SelectEspecialidades name="especialidadesIds" value={values.especialidadesIds} onChange={handleChange} />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* PDFs */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>
                    Generar PDFs
                  </Typography>

                  <Box mt={1} display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    {/* Solicitud */}
                    {modoTraslado ? (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => window.open(enlaceTraslado, "_blank", "noopener,noreferrer")}
                          disabled={!values.estado}
                        >
                          PDF Solicitud de Traslado
                        </Button>
                        <Typography variant="body2">
                          Si no se puede autocompletar, descárgalo y rellénalo manualmente.
                        </Typography>
                        <Link href={enlaceTraslado} target="_blank" rel="noopener noreferrer">
                          Descargar plantilla (link)
                        </Link>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => generarPDFSolicitudIngreso(values)}
                        disabled={!isValid || !dirty || !!errors.dni || !!errors.iban}
                      >
                        PDF Solicitud (Ingreso)
                      </Button>
                    )}

                    {/* Registro */}
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => generarPDFRegistro(values)}
                      disabled={!isValid || !dirty || !!errors.dni || !!errors.iban}
                    >
                      PDF Autorización consulta título
                    </Button>

                    {/* SEPA */}
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={() => generarPDFSepa(values)}
                      disabled={!isValid || !dirty || !!errors.dni || !!errors.iban}
                    >
                      PDF Autorización SEPA
                    </Button>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* DOCUMENTACIÓN */}
                  <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>
                    Documentación obligatoria {modoTraslado ? "(3 documentos)" : "(8 documentos)"}
                  </Typography>

                  <Box display="grid" gridTemplateColumns="1fr" gap={2}>
                    {requeridos.map((name) => (
                      <Box key={name}>
                        <Typography sx={{ mb: 0.5 }}>{etiquetasArchivos[name]}</Typography>
                        <input type="file" name={name} onChange={(e) => handleFileChange(e, name, setArchivos)} />
                        {archivos[name] && (
                          <Typography variant="caption">Seleccionado: {archivos[name].name}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <InformacionProteccionDatos />
                  </Box>

                  {/* Enviar */}
                  <Box mt={4} display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleRegistroYSubida(values)}
                      disabled={!isValid || !dirty || bloqueaPorNif || !allFilesPresent || subiendo}
                      startIcon={<CloudUploadIcon />}
                    >
                      {subiendo ? "Enviando..." : "Enviar Solicitud"}
                    </Button>
                    {subiendo && <CircularProgress size={24} />}
                  </Box>

                  {(!isValid || !!errors.dni || !!errors.iban) && (
                    <Typography color="error" fontSize="0.9rem" mt={1}>
                      Revisa que el IBAN y el documento de identidad sean válidos antes de continuar.
                    </Typography>
                  )}

                  {esAltaNuevoIngreso && nifExiste && (
                    <Typography color="error" fontSize="0.9rem" mt={1}>
                      ❌ Alta Nuevo Ingreso: ya existe un colegiado con este NIF.
                    </Typography>
                  )}

                  {!esAltaNuevoIngreso && nifExiste && (
                    <Typography color="warning.main" fontSize="0.9rem" mt={1}>
                      ℹ️ El NIF ya existe: se enviará por el trámite de actualización (/publico/reingreso).
                    </Typography>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Paper>
      </Container>
    </Box>
  );
};

export default AltaNuevoIngreso;
